import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parseDate } from "@/lib/dates";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, Copy, ExternalLink, Trash2, Plus, Loader2, Users as UsersIcon,
  Anchor, Archive, CheckCircle2, XCircle, Lock, Unlock, AlertCircle, CreditCard,
  Calendar, MapPin, GripVertical, X, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";
import { uid } from "./builder/utils";
import ClientTextsTab from "./builder/ClientTextsTab";
import AccommodationsTab from "./builder/AccommodationsTab";
import CuratedStatsTab from "./builder/CuratedStatsTab";
import { PhotoStrip, UploadPhotoButton } from "./builder/PhotoUploader";

// ---------------------------------------------------------------------------
// Curated Trip Builder (Fase 4, 2026-09-15)
// ---------------------------------------------------------------------------
// Focused editor for curated small-group trips. Reuses the shared brand
// styles / small helpers from /pages/builder/utils.js but avoids pulling in
// the itinerary-specific machinery (drag&drop across days, catalog picker,
// FX converter, Sofi push, refunds…) that don't apply to a fixed-departure
// product with externally-set prices.

const STATUS_ACTIONS = {
  draft:     [{ target: "published", label: "Publicar",   klass: "bg-pine text-white hover:bg-pine/90" }],
  published: [{ target: "closed",    label: "Cerrar ventas", klass: "bg-clay-500 text-white hover:bg-clay-600" }],
  closed:    [{ target: "published", label: "Reabrir",     klass: "bg-pine text-white hover:bg-pine/90" }],
};

const fmtMoney = (n, ccy) => {
  const currency = (ccy || "USD").toUpperCase();
  return new Intl.NumberFormat(currency === "EUR" ? "es-ES" : "en-US", {
    style: "currency", currency,
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n || 0);
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return parseDate(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
};

// Booking status pill copy + colour. Kept in sync with the model.
const BOOKING_BADGE = {
  hold:      { label: "Hold",       klass: "bg-amber-100 text-amber-800" },
  secured:   { label: "Confirmado", klass: "bg-emerald-100 text-emerald-800" },
  paid:      { label: "Pagado",     klass: "bg-pine text-white" },
  expired:   { label: "Expirado",   klass: "bg-clay-200 text-clay-700" },
  cancelled: { label: "Cancelado",  klass: "bg-clay-300 text-clay-800" },
  refunded:  { label: "Refunded",   klass: "bg-terracotta/20 text-terracotta" },
};

export default function CuratedTripBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(
    () => new URLSearchParams(window.location.search).get("tab") || "settings",
  ); // settings | itinerary | hotels | texts | bookings | stats
  const saveTimer = useRef(null);
  // Snapshot to compare against for autosave — avoids re-PATCHing when
  // nothing changed (e.g. after a load).
  const lastSaved = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/curated-trips/${id}`);
      setTrip(data);
      lastSaved.current = JSON.stringify(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos cargar el curated trip");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Debounced autosave: whenever `trip` changes, wait 900ms then PATCH the
  // delta. Mirrors ItineraryBuilder's UX so agents don't need to hit save.
  const scheduleSave = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!next) return;
      const snap = JSON.stringify(next);
      if (snap === lastSaved.current) return;
      setSaving(true);
      try {
        const payload = pickEditableFields(next);
        const { data } = await api.patch(`/curated-trips/${id}`, payload);
        lastSaved.current = JSON.stringify(data);
        // Merge back the server response so derived fields (duration_days)
        // stay in sync without a full reload.
        setTrip((t) => t ? { ...t, ...data } : data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Guardado falló");
      } finally { setSaving(false); }
    }, 900);
  }, [id]);

  const updateTrip = (patch) => {
    setTrip((t) => {
      const next = typeof patch === "function" ? patch(t) : { ...t, ...patch };
      scheduleSave(next);
      return next;
    });
  };

  const handleStatusChange = async (target) => {
    if (target === "published" && !trip.days?.length) {
      const ok = await confirmAsync({
        title: "Publicar sin itinerario",
        body: "Este viaje no tiene días añadidos aún. ¿Quieres publicarlo igual?",
        confirmText: "Publicar",
      });
      if (!ok) return;
    }
    try {
      const { data } = await api.patch(`/curated-trips/${id}`, { status: target });
      setTrip((t) => ({ ...t, status: data.status }));
      toast.success(`Estado: ${data.status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  const handleArchive = async () => {
    const ok = await confirmAsync({
      title: `Archivar "${trip.name}"`,
      body: "Se ocultará de la lista. Bloqueado si hay reservas pagadas.",
      confirmText: "Archivar",
    });
    if (!ok) return;
    try {
      await api.delete(`/curated-trips/${id}`);
      toast.success("Archivado");
      navigate("/curated-trips");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo archivar");
    }
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await api.post(`/curated-trips/${id}/duplicate`);
      toast.success("Duplicado — abriendo la copia");
      navigate(`/curated-trips/${data.curated_trip_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo duplicar");
    }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}/curated/${trip.public_token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copiado"); }
    catch { window.prompt("Copia el link:", url); }
  };

  if (loading || !trip) {
    return (
      <div className="p-10 flex items-center gap-2 text-clay-600">
        <Loader2 size={14} className="animate-spin"/> Cargando…
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/curated/${trip.public_token}`;

  return (
    <div className="min-h-screen bg-clay-50/40" data-testid="curated-trip-builder">
      {/* Header */}
      <div className="bg-white border-b border-clay-300 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-baseline justify-between gap-6">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <button
                onClick={() => navigate("/curated-trips")}
                className="text-clay-600 hover:text-clay-900 shrink-0"
                data-testid="back-btn"
              >
                <ArrowLeft size={18}/>
              </button>
              <div className="min-w-0 flex-1">
                <input
                  type="text"
                  value={trip.name}
                  onChange={(e) => updateTrip({ name: e.target.value })}
                  data-testid="trip-name-input"
                  className="w-full font-serif text-2xl bg-transparent border-none focus:outline-none focus:ring-0 truncate text-clay-900"
                />
                <div className="flex items-baseline gap-3 text-[12px] text-clay-600 mt-0.5">
                  <span className="inline-flex items-center gap-1"><Anchor size={11}/> Curated · {trip.currency}</span>
                  <span>·</span>
                  <span className="tabular">{trip.seats_committed || 0}/{trip.capacity} plazas</span>
                  <span>·</span>
                  <span>{trip.duration_days} días</span>
                  {saving && (
                    <span className="ml-auto flex items-center gap-1 text-clay-500">
                      <Loader2 size={11} className="animate-spin"/> Guardando…
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* Status + actions */}
            <div className="flex items-center gap-2 shrink-0">
              {trip.status === "published" && (
                <>
                  <button
                    onClick={copyPublicLink}
                    className="inline-flex items-center gap-1.5 text-[13px] text-clay-700 hover:text-clay-900 px-3 py-2 rounded border border-clay-300 hover:border-clay-400"
                    data-testid="copy-public-btn"
                  >
                    <Copy size={13}/> Copiar link
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[13px] text-clay-700 hover:text-clay-900 px-3 py-2 rounded border border-clay-300 hover:border-clay-400"
                    data-testid="view-public-btn"
                  >
                    <ExternalLink size={13}/> Ver landing
                  </a>
                </>
              )}
              {(STATUS_ACTIONS[trip.status] || []).map((a) => (
                <button
                  key={a.target}
                  onClick={() => handleStatusChange(a.target)}
                  className={`inline-flex items-center gap-1.5 text-[13px] px-3 py-2 rounded font-medium ${a.klass}`}
                  data-testid={`status-btn-${a.target}`}
                >
                  {a.target === "published" ? <Unlock size={13}/> : <Lock size={13}/>}
                  {a.label}
                </button>
              ))}
              <button
                onClick={handleDuplicate}
                className="inline-flex items-center gap-1.5 text-[13px] text-clay-700 hover:text-clay-900 px-3 py-2 rounded border border-clay-300"
                data-testid="duplicate-btn"
              >
                <Copy size={13}/> Duplicar
              </button>
              <button
                onClick={handleArchive}
                className="inline-flex items-center gap-1.5 text-[13px] text-clay-700 hover:text-clay-900 px-3 py-2 rounded border border-clay-300"
                data-testid="archive-btn"
                title="Archivar"
              >
                <Archive size={13}/>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-6 mt-5 -mb-px">
            {[
              { id: "settings", label: "Ajustes & precio" },
              { id: "itinerary", label: "Itinerario" },
              { id: "hotels", label: `Hoteles${trip.accommodations?.length ? ` (${trip.accommodations.length})` : ""}` },
              { id: "texts", label: "Textos del cliente" },
              { id: "bookings", label: `Reservas${trip.bookings?.length ? ` (${trip.bookings.length})` : ""}` },
              { id: "stats", label: "Estadísticas" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`pb-2.5 text-sm border-b-2 transition-colors ${
                  activeTab === t.id
                    ? "border-terracotta text-clay-900 font-medium"
                    : "border-transparent text-clay-600 hover:text-clay-900"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        {activeTab === "settings" && <SettingsTab trip={trip} onChange={updateTrip}/>}
        {activeTab === "itinerary" && <ItineraryTab trip={trip} onChange={updateTrip}/>}
        {activeTab === "hotels" && <AccommodationsTab trip={trip} onChange={updateTrip}/>}
        {activeTab === "texts" && <ClientTextsTab trip={trip} onChange={updateTrip}/>}
        {activeTab === "bookings" && <BookingsTab trip={trip} reload={load}/>}
        {activeTab === "stats" && <CuratedStatsTab trip={trip}/>}
      </div>
    </div>
  );
}

// Only send the editable fields to the PATCH endpoint — pickup avoids
// server-derived fields like `seats_available` from creeping into the
// payload where they'd be silently ignored (and add noise to the logs).
function pickEditableFields(t) {
  return {
    name: t.name,
    tagline: t.tagline || null,
    description: t.description || null,
    hero_image: t.hero_image || null,
    start_date: t.start_date || null,
    end_date: t.end_date || null,
    capacity: Number(t.capacity) || 0,
    price_single: Number(t.price_single) || 0,
    price_double: Number(t.price_double) || 0,
    currency: t.currency || "USD",
    cities: t.cities || [],
    days: t.days || [],
    accommodations: t.accommodations || [],
    info_documents: t.info_documents || {},
    hotel_perks: Array.isArray(t.hotel_perks) ? t.hotel_perks : null,
    notify_emails: t.notify_emails || [],
    paypal_fee: !!t.paypal_fee,
  };
}

// ---------------------------------------------------------------------------
// Settings tab — headline data + prices + hero
// ---------------------------------------------------------------------------

function SettingsTab({ trip, onChange }) {
  return (
    <div className="grid lg:grid-cols-[2fr_1fr] gap-8" data-testid="tab-content-settings">
      <div className="space-y-6">
        <Section title="Presentación">
          <Field label="Tagline (marketing)">
            <input
              type="text" value={trip.tagline || ""}
              onChange={(e) => onChange({ tagline: e.target.value })}
              placeholder="Ex: A gentle 8-day escape through Piedmont wineries and Lake Como"
              data-testid="tagline-input"
              className={inputCls}
            />
          </Field>
          <Field label="Descripción larga">
            <textarea
              value={trip.description || ""}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              data-testid="description-input"
              placeholder="Historia del viaje, tono, qué encontrarán los viajeros…"
              className={`${inputCls} font-sans`}
            />
          </Field>
          <Field label="Imagen principal (hero)">
            <div className="flex items-center gap-2">
              <input
                type="url" value={trip.hero_image || ""}
                onChange={(e) => onChange({ hero_image: e.target.value })}
                placeholder="Sube una foto o pega una URL https://…"
                data-testid="hero-image-input"
                className={inputCls}
              />
              <UploadPhotoButton
                multiple={false}
                testid="hero-image-upload"
                onUploaded={(urls) => onChange({ hero_image: urls[0] })}
              />
            </div>
            {trip.hero_image && (
              <div className="mt-2 rounded overflow-hidden border border-clay-300 aspect-[16/7] bg-clay-100">
                <img src={trip.hero_image} alt="" className="w-full h-full object-cover"/>
              </div>
            )}
          </Field>
        </Section>

        <Section title="Ciudades destacadas">
          <CitiesEditor
            cities={trip.cities || []}
            onChange={(cities) => onChange({ cities })}
          />
        </Section>
      </div>

      <div className="space-y-6">
        <Section title="Fechas & cupo">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio">
              <input
                type="date" value={trip.start_date || ""}
                onChange={(e) => onChange({ start_date: e.target.value })}
                data-testid="start-date-input"
                className={inputCls}
              />
            </Field>
            <Field label="Fin">
              <input
                type="date" value={trip.end_date || ""}
                onChange={(e) => onChange({ end_date: e.target.value })}
                data-testid="end-date-input"
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="Cupo total (plazas)">
            <input
              type="number" min="1" max="100"
              value={trip.capacity} onChange={(e) => onChange({ capacity: Number(e.target.value) || 0 })}
              data-testid="capacity-input"
              className={inputCls}
            />
            <div className="text-[11px] text-clay-500 mt-1">
              {trip.seats_committed || 0} comprometidas · {(trip.capacity - (trip.seats_committed || 0))} libres
            </div>
          </Field>
        </Section>

        <Section title="Precios / persona">
          <Field label="Moneda">
            <select
              value={trip.currency} onChange={(e) => onChange({ currency: e.target.value })}
              data-testid="currency-input"
              className={`${inputCls} bg-white`}
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </Field>
          <Field label="Habitación individual">
            <input
              type="number" min="0" value={trip.price_single}
              onChange={(e) => onChange({ price_single: Number(e.target.value) || 0 })}
              data-testid="price-single-input"
              className={inputCls}
            />
          </Field>
          <Field label="Habitación doble (compartida)">
            <input
              type="number" min="0" value={trip.price_double}
              onChange={(e) => onChange({ price_double: Number(e.target.value) || 0 })}
              data-testid="price-double-input"
              className={inputCls}
            />
          </Field>
          <div className="text-[11px] text-clay-500 leading-relaxed pt-1">
            Se aplican los mismos plazos que en itinerarios: <strong>30% depósito</strong> si faltan &gt; 60 días,
            saldo antes de 60 días de la salida; <strong>100%</strong> obligatorio si faltan ≤60 días.
          </div>
        </Section>

        <Section title="Avisos de reserva">
          <Field label="Emails que reciben el aviso al pagar una plaza">
            <NotifyEmailsEditor
              emails={trip.notify_emails || []}
              onChange={(notify_emails) => onChange({ notify_emails })}
            />
            <div className="text-[11px] text-clay-500 mt-2 leading-relaxed">
              Al capturarse un pago, estos correos reciben el aviso interno
              (comprador, importe, plazas, habitación y saldo pendiente).
              El comprador recibe siempre su recibo automático.
              {(trip.notify_emails || []).length === 0 && (
                <span className="block mt-1 text-terracotta">
                  Sin destinatarios: nadie recibirá el aviso interno.
                </span>
              )}
            </div>
          </Field>
        </Section>

        <Section title="Link público">
          <div className="text-[13px] text-clay-700 space-y-2">
            {trip.status === "published" ? (
              <>
                <div className="p-2 bg-clay-100 rounded text-[12px] break-all font-mono">
                  {`${window.location.origin}/curated/${trip.public_token}`}
                </div>
                <div className="text-[11px] text-clay-500">
                  Publicado. Cualquiera con el link puede reservar plazas.
                </div>
              </>
            ) : (
              <div className="text-[12px] text-clay-500 italic">
                Publica el viaje para activar el link.
              </div>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

function NotifyEmailsEditor({ emails, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(v)) return;
    if (!emails.includes(v)) onChange([...emails, v]);
    setInput("");
  };
  return (
    <div className="space-y-2" data-testid="notify-emails-editor">
      <div className="flex gap-2">
        <input
          type="email" value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          onBlur={add}
          placeholder="marina@espiritutravel.com (Enter para añadir)"
          data-testid="notify-email-input"
          className={inputCls}
        />
        <button onClick={add} data-testid="notify-email-add" className="px-3 py-2 border border-clay-300 rounded text-sm hover:bg-clay-100">
          <Plus size={13}/>
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {emails.map((e) => (
          <span key={e} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-clay-100 text-[12px] text-clay-800" data-testid={`notify-email-${e}`}>
            {e}
            <button
              onClick={() => onChange(emails.filter((x) => x !== e))}
              data-testid={`notify-email-remove-${e}`}
              className="text-clay-500 hover:text-clay-900"
              title="Quitar"
            >
              <XCircle size={11}/>
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function CitiesEditor({ cities, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v || cities.includes(v)) return;
    onChange([...cities, v]);
    setInput("");
  };
  return (
    <div className="space-y-2" data-testid="cities-editor">
      <div className="flex gap-2">
        <input
          type="text" value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Añade una ciudad (Enter para confirmar)"
          className={inputCls}
        />
        <button onClick={add} className="px-3 py-2 border border-clay-300 rounded text-sm hover:bg-clay-100">
          <Plus size={13}/>
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {cities.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-clay-100 text-[12px] text-clay-800">
            {c}
            <button
              onClick={() => onChange(cities.filter((x) => x !== c))}
              className="text-clay-500 hover:text-clay-900"
              title="Quitar"
            >
              <XCircle size={11}/>
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Itinerary tab — light-weight day + service editor.
// Intentional simplification vs the full itinerary builder: no drag&drop
// re-ordering across days, no catalog picker, no cost math. Curated trips
// don't need any of that (prices are set externally). If future demand
// grows we can plug in the shared DayBlock component later.
// ---------------------------------------------------------------------------

function ItineraryTab({ trip, onChange }) {
  const days = trip.days || [];

  const addDay = () => {
    const last = days[days.length - 1];
    const nextDate = (() => {
      if (!last?.date) return trip.start_date || "";
      try {
        const d = new Date(last.date);
        d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
      } catch { return ""; }
    })();
    onChange({
      days: [...days, {
        day_id: `d_${uid()}`,
        date: nextDate,
        city: last?.city || "",
        services: [],
      }],
    });
  };

  const updateDay = (day_id, patch) => {
    onChange({
      days: days.map((d) => d.day_id === day_id ? { ...d, ...patch } : d),
    });
  };

  const removeDay = async (day_id) => {
    const ok = await confirmAsync({
      title: "Eliminar día", body: "¿Seguro? Se perderán las actividades de ese día.",
      confirmText: "Eliminar",
    });
    if (!ok) return;
    onChange({ days: days.filter((d) => d.day_id !== day_id) });
  };

  return (
    <div className="space-y-6" data-testid="tab-content-itinerary">
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-clay-600">
          {days.length} día{days.length !== 1 ? "s" : ""} en el itinerario
        </div>
        <button
          onClick={addDay}
          data-testid="add-day-btn"
          className="inline-flex items-center gap-1.5 bg-clay-900 hover:bg-black text-white px-3 py-2 rounded text-[13px]"
        >
          <Plus size={13}/> Añadir día
        </button>
      </div>

      {days.length === 0 ? (
        <div className="border border-dashed border-clay-300 rounded p-10 text-center text-clay-600 text-sm">
          Aún no hay días en el itinerario. Empieza añadiendo el primer día.
        </div>
      ) : (
        <div className="space-y-4">
          {days.map((d, idx) => (
            <DayEditor
              key={d.day_id}
              day={d}
              index={idx}
              onUpdate={(patch) => updateDay(d.day_id, patch)}
              onRemove={() => removeDay(d.day_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DayEditor({ day, index, onUpdate, onRemove }) {
  // The backend model calls it `service_id` (an `id` key sent from here
  // would be dropped by Pydantic on save, leaving every row with an
  // undefined identifier — which used to patch ALL services at once).
  const sid = (s) => s.service_id || s.id;
  const addService = () => {
    onUpdate({
      services: [...(day.services || []), {
        service_id: `svc_${uid()}`,
        type: "actividad",
        name: "",
        description: "",
        time: "",
        duration: "",
        image_urls: [],
      }],
    });
  };
  const updateService = (id, patch) => {
    onUpdate({
      services: (day.services || []).map((s) => sid(s) === id ? { ...s, ...patch } : s),
    });
  };
  const removeService = (id) => {
    onUpdate({ services: (day.services || []).filter((s) => sid(s) !== id) });
  };
  return (
    <div className="bg-white border border-clay-300 rounded-md p-5" data-testid={`day-editor-${day.day_id}`}>
      <div className="flex items-center gap-4 pb-4 border-b border-clay-200 flex-wrap">
        <div className="smallcaps text-terracotta shrink-0">Día {index + 1}</div>
        <input
          type="date" value={day.date || ""}
          onChange={(e) => onUpdate({ date: e.target.value })}
          data-testid={`day-date-${day.day_id}`}
          className="text-sm border border-clay-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-terracotta"
        />
        <input
          type="text" value={day.city || ""}
          onChange={(e) => onUpdate({ city: e.target.value })}
          placeholder="Ciudad"
          data-testid={`day-city-${day.day_id}`}
          className="w-40 text-sm border border-clay-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-terracotta"
        />
        <input
          type="text" value={day.label || ""}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder="Título del día (ej: Welcome to Sicily)"
          data-testid={`day-label-${day.day_id}`}
          className="flex-1 text-sm border border-clay-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-terracotta"
        />
        <button
          onClick={onRemove}
          className="text-clay-500 hover:text-terracotta"
          title="Eliminar día"
          data-testid={`day-remove-${day.day_id}`}
        >
          <Trash2 size={14}/>
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {(day.services || []).map((s) => (
          <ServiceEditor key={sid(s)} service={s} onUpdate={(p) => updateService(sid(s), p)} onRemove={() => removeService(sid(s))}/>
        ))}
        <button
          onClick={addService}
          data-testid={`add-service-${day.day_id}`}
          className="w-full py-2 border border-dashed border-clay-300 rounded text-[13px] text-clay-600 hover:border-terracotta hover:text-terracotta inline-flex items-center justify-center gap-1.5"
        >
          <Plus size={13}/> Añadir actividad
        </button>
      </div>
    </div>
  );
}

function ServiceEditor({ service, onUpdate, onRemove }) {
  const photos = (service.image_urls || []).filter(Boolean);
  return (
    <div className="border border-clay-200 rounded p-3 bg-clay-50/40" data-testid={`service-${service.service_id || service.id}`}>
      <div className="grid grid-cols-[110px_1fr_100px_100px_28px] gap-2 items-start">
        <select
          value={service.type || "actividad"}
          onChange={(e) => onUpdate({ type: e.target.value })}
          className={smallInputCls}
        >
          <option value="actividad">Experience</option>
          <option value="entradas">Tickets</option>
          <option value="transfer">Transfer</option>
          <option value="tren">Train</option>
          <option value="vuelo">Flight</option>
        </select>
        <input
          type="text" value={service.name || ""}
          onChange={(e) => onUpdate({ name: e.target.value })}
          placeholder="Nombre de la actividad"
          className={smallInputCls}
        />
        <input
          type="text" value={service.time || ""}
          onChange={(e) => onUpdate({ time: e.target.value })}
          placeholder="Hora"
          className={smallInputCls}
        />
        <input
          type="text" value={service.duration || ""}
          onChange={(e) => onUpdate({ duration: e.target.value })}
          placeholder="Duración"
          className={smallInputCls}
        />
        <button onClick={onRemove} className="text-clay-500 hover:text-terracotta py-1" title="Quitar">
          <Trash2 size={13}/>
        </button>
      </div>
      <textarea
        value={service.description || ""}
        onChange={(e) => onUpdate({ description: e.target.value })}
        rows={2}
        placeholder="Descripción (opcional)"
        className={`mt-2 ${smallInputCls}`}
      />
      <ServicePhotos
        photos={photos}
        onChange={(next) => onUpdate({ image_urls: next })}
        testid={`service-photos-${service.service_id || service.id}`}
      />
    </div>
  );
}

/** Photo strip for a day service — same widget as the hotel photos. */
function ServicePhotos({ photos, onChange, testid }) {
  return (
    <div className="mt-2">
      <PhotoStrip photos={photos} onChange={onChange} testid={testid}/>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bookings tab — read-only list of purchases
// ---------------------------------------------------------------------------

function BookingsTab({ trip, reload }) {
  const bookings = trip.bookings || [];
  const [extrasModalOpen, setExtrasModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // for "specific" target pre-selection
  // Ordering: most recent first, holds at the top of their date bucket
  const sorted = useMemo(() => {
    return [...bookings].sort((a, b) => {
      const at = new Date(a.created_at || 0).getTime();
      const bt = new Date(b.created_at || 0).getTime();
      return bt - at;
    });
  }, [bookings]);

  // Flat list of every extra across every booking, useful for the "sent
  // extras" section and for the buyer breakdown inside each row.
  const allExtras = useMemo(() => {
    const rows = [];
    for (const b of bookings) {
      for (const e of (b.extras || [])) {
        rows.push({ ...e, booking_id: b.booking_id, buyer_name: b.buyer_name });
      }
    }
    return rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [bookings]);

  const paidCount = bookings.filter((b) => b.status === "paid").length;
  const securedCount = bookings.filter((b) => b.status === "secured").length;
  const eligibleForExtras = paidCount + securedCount;
  // Abandoned carts: the buyer filled in the form but nothing was ever
  // captured. The agency also gets an email per cart (see server.py
  // `_notify_curated_abandoned`); this is the list to work through.
  const isUnpaid = (b) =>
    ["hold", "expired"].includes(b.status) &&
    !(b.payments || []).some((p) => p.status === "captured");
  const unpaidCount = bookings.filter(isUnpaid).length;

  // Ready-to-send nudge for an abandoned cart — opens the agent's mail
  // client with the buyer's address and the public link already in place.
  const nudgeHref = (b) => {
    const url = `${window.location.origin}/curated/${trip.public_token}`;
    const first = (b.buyer_name || "").split(" ")[0] || "there";
    const subject = `Your spot on ${trip.name}`;
    const body =
      `Hi ${first},\n\n` +
      `I saw you started a reservation for ${trip.name} (${b.seats} traveller${b.seats === 1 ? "" : "s"}) ` +
      `but the payment wasn't completed, so your seats were released.\n\n` +
      `There's still availability — you can pick them up again here:\n${url}\n\n` +
      `If you'd rather I hold them for you or you have any questions, just reply to this email.\n\n` +
      `Best,\nMarina · Espíritu Travel\n`;
    return `mailto:${b.buyer_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const totalCaptured = bookings.reduce((sum, b) => {
    const captured = (b.payments || []).filter((p) => p.status === "captured");
    return sum + captured.reduce((s, p) => s + (p.amount_eur || 0), 0);
  }, 0);

  const openSpecificModal = (bookingId) => {
    setSelectedIds([bookingId]);
    setExtrasModalOpen(true);
  };
  const openAllModal = () => {
    setSelectedIds([]);
    setExtrasModalOpen(true);
  };

  if (bookings.length === 0) {
    return (
      <div className="border border-dashed border-clay-300 rounded p-10 text-center text-clay-600" data-testid="tab-content-bookings">
        <UsersIcon size={28} className="mx-auto text-clay-500 mb-3"/>
        <p className="text-sm">Aún no hay reservas. Cuando alguien complete la reserva en la landing pública, aparecerá aquí.</p>
      </div>
    );
  }

  return (
    <div data-testid="tab-content-bookings">
      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        <SummaryTile label="Reservas totales" value={bookings.length}/>
        <SummaryTile label="Pagadas" value={paidCount}/>
        <SummaryTile label="Depósito capturado" value={securedCount}/>
        <SummaryTile label="Sin pagar" value={unpaidCount}/>
        <SummaryTile label="Recaudado" value={fmtMoney(totalCaptured, trip.currency)}/>
      </div>

      {/* Extras CTA */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="smallcaps text-clay-700">Reservas</h3>
        <button
          onClick={openAllModal}
          disabled={eligibleForExtras === 0}
          data-testid="add-extra-btn"
          className="inline-flex items-center gap-1.5 bg-clay-900 hover:bg-black disabled:bg-clay-300 disabled:cursor-not-allowed text-white px-3 py-2 rounded text-[13px]"
          title={eligibleForExtras === 0 ? "Ninguna reserva pagada/confirmada todavía" : "Enviar un extra a compradores"}
        >
          <Plus size={13}/> Añadir extra
        </button>
      </div>

      <div className="border border-clay-300 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-clay-100 border-b border-clay-300">
            <tr className="text-left text-[11px] uppercase tracking-wider text-clay-700">
              <th className="px-4 py-3 font-medium">Comprador</th>
              <th className="px-4 py-3 font-medium">Plazas · Hab.</th>
              <th className="px-4 py-3 font-medium">Total</th>
              <th className="px-4 py-3 font-medium">Pagado</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium">Extras</th>
              <th className="px-4 py-3 font-medium">Extensiones</th>
              <th className="px-4 py-3 font-medium">Creada</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((b) => {
              const captured = (b.payments || []).filter((p) => p.status === "captured");
              const paidAmt = captured.reduce((s, p) => s + (p.amount_eur || 0), 0);
              const rooms = (b.room_selections || []).reduce((acc, r) => {
                acc[r.room_type] = (acc[r.room_type] || 0) + 1;
                return acc;
              }, {});
              const roomsLine = Object.entries(rooms).map(([k, n]) => `${n} × ${k}`).join(" · ");
              const paidStatus = b.status === "paid" || b.status === "secured";
              const extras = b.extras || [];
              return (
                <tr key={b.booking_id} className="border-b border-clay-200 last:border-b-0 align-top" data-testid={`booking-row-${b.booking_id}`}>
                  <td className="px-4 py-3">
                    <div className="text-clay-900 font-medium">{b.buyer_name}</div>
                    <div className="text-[12px] text-clay-600">{b.buyer_email}</div>
                    {attributionLabel(b.attribution) && (
                      <div className="text-[10px] text-clay-500 mt-0.5" data-testid={`booking-attribution-${b.booking_id}`} title={JSON.stringify(b.attribution)}>
                        via {attributionLabel(b.attribution)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-clay-700 whitespace-nowrap tabular">
                    {b.seats} · <span className="text-clay-600 text-[12px]">{roomsLine}</span>
                  </td>
                  <td className="px-4 py-3 tabular whitespace-nowrap">{fmtMoney(b.total_amount, b.currency)}</td>
                  <td className="px-4 py-3 tabular whitespace-nowrap">
                    {paidAmt > 0 ? (
                      <span className="text-emerald-700 font-medium">{fmtMoney(paidAmt, b.currency)}</span>
                    ) : (
                      <span className="text-clay-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${BOOKING_BADGE[b.status]?.klass || "bg-clay-200 text-clay-800"}`}>
                      {BOOKING_BADGE[b.status]?.label || b.status}
                    </span>
                    {b.status === "hold" && b.hold_expires_at && (
                      <div className="text-[10px] text-amber-700 mt-1 tabular">
                        exp {fmtDateTime(b.hold_expires_at)}
                      </div>
                    )}
                    {isUnpaid(b) && b.buyer_email && (
                      <a
                        href={nudgeHref(b)}
                        data-testid={`nudge-buyer-${b.booking_id}`}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-terracotta hover:underline"
                        title="Escribir al comprador con el link del viaje"
                      >
                        <Mail size={11}/> Escribirle
                      </a>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {paidStatus ? (
                      <div className="space-y-1 min-w-[180px]">
                        {extras.length === 0 ? (
                          <button
                            onClick={() => openSpecificModal(b.booking_id)}
                            data-testid={`add-extra-to-${b.booking_id}`}
                            className="text-[12px] text-terracotta hover:text-clay-900 inline-flex items-center gap-1"
                          >
                            <Plus size={11}/> Añadir extra
                          </button>
                        ) : (
                          <>
                            {extras.map((e) => (
                              <BookingExtraRow
                                key={e.extra_id}
                                extra={e}
                                trip={trip}
                                booking={b}
                                reload={reload}
                              />
                            ))}
                            <button
                              onClick={() => openSpecificModal(b.booking_id)}
                              className="text-[11px] text-clay-600 hover:text-terracotta inline-flex items-center gap-1"
                              data-testid={`add-another-extra-${b.booking_id}`}
                            >
                              <Plus size={10}/> otro
                            </button>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="text-clay-400 text-[12px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {paidStatus ? (
                      <ExtensionsCell trip={trip} booking={b} />
                    ) : (
                      <span className="text-clay-400 text-[12px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-clay-600 whitespace-nowrap tabular">
                    {fmtDateTime(b.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-right">
        <button
          onClick={reload}
          className="text-[12px] text-clay-600 hover:text-clay-900 underline"
          data-testid="reload-bookings"
        >
          Refrescar
        </button>
      </div>

      {extrasModalOpen && (
        <CreateExtraModal
          trip={trip}
          bookings={bookings.filter((b) => b.status === "paid" || b.status === "secured")}
          preselectedIds={selectedIds}
          onCancel={() => setExtrasModalOpen(false)}
          onCreated={() => { setExtrasModalOpen(false); reload(); }}
        />
      )}
    </div>
  );
}

// One-line row for an extra attached to a booking. Shows title +
// amount + status pill + copy-link button so the agent can WhatsApp
// the payment URL to the buyer directly.
function BookingExtraRow({ extra, trip, booking, reload }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/pay/extra/${extra.payment_token}`;
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link del extra copiado"); }
    catch { window.prompt("Copia el link del extra:", url); }
  };
  const doDelete = async () => {
    const ok = await confirmAsync({
      title: "Eliminar extra",
      body: extra.status === "paid"
        ? "Este extra ya fue pagado. Se marcará como cancelado (el dinero sigue en PayPal — considera hacer un refund)."
        : "¿Seguro? Se eliminará del comprador.",
      confirmText: extra.status === "paid" ? "Cancelar" : "Eliminar",
    });
    if (!ok) return;
    try {
      await api.delete(`/curated-trips/${trip.curated_trip_id}/extras/${extra.extra_id}`);
      toast.success("Extra eliminado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };
  const statusColor = extra.status === "paid" ? "text-emerald-700" :
                      extra.status === "cancelled" ? "text-clay-400" :
                      "text-clay-700";
  return (
    <div className="flex items-center gap-1.5 text-[12px]" data-testid={`extra-row-${extra.extra_id}`}>
      <span className={`${statusColor} truncate max-w-[140px]`} title={extra.title}>
        {extra.title}
      </span>
      <span className="text-clay-500 tabular shrink-0">{fmtMoney(extra.amount_eur, extra.currency)}</span>
      {extra.status === "paid" && <CheckCircle2 size={11} className="text-emerald-600 shrink-0"/>}
      {extra.status === "cancelled" && <XCircle size={11} className="text-clay-400 shrink-0"/>}
      <button
        onClick={doCopy}
        className="ml-auto p-0.5 hover:bg-clay-200 rounded shrink-0"
        title="Copiar link"
        data-testid={`copy-extra-link-${extra.extra_id}`}
      >
        <Copy size={10} className={copied ? "text-emerald-600" : "text-clay-500"}/>
      </button>
      <button
        onClick={doDelete}
        className="p-0.5 hover:bg-clay-200 rounded shrink-0"
        title="Eliminar"
        data-testid={`delete-extra-${extra.extra_id}`}
      >
        <Trash2 size={10} className="text-clay-500 hover:text-terracotta"/>
      </button>
    </div>
  );
}

// Extensions cell (Fase 6) — per-booking action to spawn a linked
// pre/post itinerary. Lists any existing extensions with a link to
// their builder, plus two "+ Pre" / "+ Post" buttons.
function ExtensionsCell({ trip, booking }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null); // null = not loaded yet
  const [busy, setBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/curated-trips/${trip.curated_trip_id}/bookings/${booking.booking_id}/extensions`,
      );
      setRows(data.extensions || []);
    } catch {
      setRows([]);
    }
  }, [trip.curated_trip_id, booking.booking_id]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const createExtension = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/curated-trips/${trip.curated_trip_id}/bookings/${booking.booking_id}/extensions`,
        { kind },
      );
      toast.success(`${kind === "pre" ? "Pre-extensión" : "Post-extensión"} creada`);
      navigate(`/itineraries/${data.itinerary_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la extensión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1 min-w-[170px]" data-testid={`extensions-cell-${booking.booking_id}`}>
      {rows && rows.length > 0 && rows.map((ext) => (
        <button
          key={ext.itinerary_id}
          onClick={() => navigate(`/itineraries/${ext.itinerary_id}`)}
          className="w-full text-left text-[12px] text-clay-700 hover:text-terracotta group flex items-center gap-1.5"
          data-testid={`open-extension-${ext.itinerary_id}`}
        >
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium ${ext.extension_kind === "pre" ? "bg-sky-100 text-sky-800" : "bg-violet-100 text-violet-800"}`}>
            {ext.extension_kind === "pre" ? "PRE" : "POST"}
          </span>
          <span className="truncate group-hover:underline">
            {ext.name || "Extensión sin nombre"}
          </span>
        </button>
      ))}
      <div className="flex items-center gap-1.5 pt-0.5">
        <button
          onClick={() => createExtension("pre")}
          disabled={busy}
          className="text-[11px] text-sky-800 hover:text-clay-900 inline-flex items-center gap-0.5 disabled:opacity-50"
          data-testid={`add-pre-extension-${booking.booking_id}`}
          title="Añadir una pre-extensión (viaje antes del grupo)"
        >
          <Plus size={10}/> Pre
        </button>
        <span className="text-clay-300">·</span>
        <button
          onClick={() => createExtension("post")}
          disabled={busy}
          className="text-[11px] text-violet-800 hover:text-clay-900 inline-flex items-center gap-0.5 disabled:opacity-50"
          data-testid={`add-post-extension-${booking.booking_id}`}
          title="Añadir una post-extensión (viaje después del grupo)"
        >
          <Plus size={10}/> Post
        </button>
      </div>
    </div>
  );
}



// Create-extra modal — pick target (all_paid vs specific) + title +
// description + amount. Server auto-generates one payment_token per
// selected booking so buyers only see and pay for their own extras.
function CreateExtraModal({ trip, bookings, preselectedIds, onCancel, onCreated }) {
  const [target, setTarget] = useState(preselectedIds.length > 0 ? "specific" : "all_paid");
  const [selected, setSelected] = useState(new Set(preselectedIds));
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = title.trim().length >= 2 && Number(amount) > 0 &&
    (target === "all_paid" ? bookings.length > 0 : selected.size > 0);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/curated-trips/${trip.curated_trip_id}/extras`, {
        title: title.trim(),
        description: description.trim(),
        amount_eur: Number(amount),
        target,
        booking_ids: target === "specific" ? Array.from(selected) : undefined,
      });
      toast.success(`Extra enviado a ${data.count} comprador${data.count !== 1 ? "es" : ""}`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el extra");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-clay-900/40 flex items-center justify-center p-6"
      onClick={onCancel}
      data-testid="create-extra-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-md shadow-xl w-full max-w-lg"
      >
        <div className="px-6 py-4 border-b border-clay-200">
          <h2 className="font-serif text-2xl">Nuevo extra</h2>
          <p className="text-[13px] text-clay-600 mt-1">
            Cada comprador recibirá su propio link de pago con el importe indicado.
          </p>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className="smallcaps text-clay-700">Título del extra *</span>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="extra-title" autoFocus required
              placeholder="e.g. Private wine tasting upgrade"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="smallcaps text-clay-700">Descripción (opcional)</span>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="extra-description"
              placeholder="Detalle que verá el comprador en su link…"
              className={inputCls}
            />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="smallcaps text-clay-700">Precio *</span>
              <div className="mt-1 flex items-center border border-clay-300 rounded overflow-hidden">
                <span className="px-2 py-2 bg-clay-100 text-[12px] text-clay-600">{trip.currency}</span>
                <input
                  type="number" min="0" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="extra-amount"
                  placeholder="0"
                  className="flex-1 px-3 py-2 text-sm focus:outline-none"
                />
              </div>
            </label>
            <div/>
          </div>

          <div>
            <span className="smallcaps text-clay-700 block mb-2">Destinatarios</span>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer p-2 border border-clay-300 rounded hover:bg-clay-50" data-testid="target-all-paid">
                <input
                  type="radio" name="target" checked={target === "all_paid"}
                  onChange={() => setTarget("all_paid")}
                  className="mt-1 accent-terracotta"
                />
                <div className="flex-1">
                  <div className="text-sm text-clay-900">Todas las reservas pagadas / confirmadas</div>
                  <div className="text-[11px] text-clay-600 mt-0.5">
                    {bookings.length} comprador{bookings.length !== 1 ? "es" : ""} elegible{bookings.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2 border border-clay-300 rounded hover:bg-clay-50" data-testid="target-specific">
                <input
                  type="radio" name="target" checked={target === "specific"}
                  onChange={() => setTarget("specific")}
                  className="mt-1 accent-terracotta"
                />
                <div className="flex-1">
                  <div className="text-sm text-clay-900">Compradores específicos</div>
                  <div className="text-[11px] text-clay-600 mt-0.5">
                    Selecciona uno o varios abajo
                  </div>
                </div>
              </label>
              {target === "specific" && (
                <div className="ml-6 mt-2 space-y-1 max-h-48 overflow-y-auto border border-clay-200 rounded p-2">
                  {bookings.length === 0 ? (
                    <div className="text-[12px] text-clay-500 italic">Aún no hay reservas pagadas</div>
                  ) : bookings.map((b) => (
                    <label key={b.booking_id} className="flex items-center gap-2 cursor-pointer py-1 text-[13px]" data-testid={`select-booking-${b.booking_id}`}>
                      <input
                        type="checkbox"
                        checked={selected.has(b.booking_id)}
                        onChange={() => toggle(b.booking_id)}
                        className="accent-terracotta"
                      />
                      <span className="text-clay-900">{b.buyer_name}</span>
                      <span className="text-clay-500">·</span>
                      <span className="text-clay-600 tabular">{b.seats} plaza{b.seats !== 1 ? "s" : ""}</span>
                      <span className={`ml-auto inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${BOOKING_BADGE[b.status]?.klass || ""}`}>
                        {BOOKING_BADGE[b.status]?.label || b.status}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-clay-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-clay-700 hover:text-clay-900 px-3 py-2">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="submit-extra"
            className="inline-flex items-center gap-2 bg-clay-900 hover:bg-black disabled:bg-clay-400 text-white px-4 py-2.5 rounded text-sm font-medium"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin"/> Enviando…</> : <>Enviar extra</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bits & pieces
// ---------------------------------------------------------------------------

const inputCls = "w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta";
const smallInputCls = "w-full border border-clay-300 rounded px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-terracotta";

// "via facebook · spring-2026" from the utm_* / click-id the buyer landed with.
function attributionLabel(attr) {
  if (!attr) return null;
  const source = attr.utm_source || (attr.fbclid ? "facebook" : attr.gclid ? "google" : attr.ttclid ? "tiktok" : null);
  const ref = !source && attr.referrer ? attr.referrer.replace(/^https?:\/\//, "").split("/")[0] : null;
  const head = source || ref;
  if (!head) return null;
  return attr.utm_campaign ? `${head} · ${attr.utm_campaign}` : head;
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-clay-300 rounded-md p-5">
      <h3 className="smallcaps text-clay-700 mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[12px] text-clay-700 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="bg-white border border-clay-300 rounded-md p-4">
      <div className="text-[11px] uppercase tracking-wider text-clay-600">{label}</div>
      <div className="mt-1 font-serif text-2xl text-clay-900 tabular">{value}</div>
    </div>
  );
}
